import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api-client';
import { CreditCard, ArrowUpRight, Plus, Trash2, CheckCircle, Shield } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const cardBrandIcons = {
  Visa: (
    <svg viewBox="0 0 48 32" className="w-10 h-7"><rect width="48" height="32" rx="4" fill="#1A1F71"/><path d="M19.5 21h-3l1.9-11.5h3L19.5 21zm8.2-11.5l-2.8 7.9-.3-1.6-1-5.1s-.1-1.2-1.5-1.2h-4.7l-.1.3s1.6.3 3.4 1.5l2.8 10.7h3.1l4.8-12.5h-3.7zm15.1 11.5h2.7l-2.4-11.5h-2.4c-1.1 0-1.4.6-1.4.6l-4.4 10.9h3.1l.6-1.7h3.8l.4 1.7zm-3.3-4l1.6-4.3.9 4.3h-2.5zM29 13.2l.4-2.5s-1.3-.5-2.6-.5c-1.4 0-4.8.6-4.8 3.6 0 2.8 3.9 2.8 3.9 4.3 0 1.5-3.5 1.2-4.6.3l-.4 2.6s1.3.6 3.3.6c2 0 4.9-1 4.9-3.7 0-2.8-3.9-3.1-3.9-4.3 0-1.2 2.7-1 3.8-.4z" fill="#fff"/></svg>
  ),
  Mastercard: (
    <svg viewBox="0 0 48 32" className="w-10 h-7"><rect width="48" height="32" rx="4" fill="#252525"/><circle cx="19" cy="16" r="9" fill="#EB001B"/><circle cx="29" cy="16" r="9" fill="#F79E1B"/><path d="M24 9.2a9 9 0 0 1 3.3 6.8 9 9 0 0 1-3.3 6.8 9 9 0 0 1-3.3-6.8A9 9 0 0 1 24 9.2z" fill="#FF5F00"/></svg>
  ),
  Amex: (
    <svg viewBox="0 0 48 32" className="w-10 h-7"><rect width="48" height="32" rx="4" fill="#2E77BC"/><text x="24" y="19" fill="#fff" fontSize="9" fontFamily="monospace" textAnchor="middle" fontWeight="bold">AMEX</text></svg>
  ),
};

function PaymentMethodModal({ open, onClose, currentMethod, onSave }) {
  const [step, setStep] = useState('list');
  const [cards, setCards] = useState([
    { id: 'pm_1', brand: currentMethod?.brand || 'Visa', last4: currentMethod?.last4 || '4242', expiry: '12/2027', isDefault: true },
  ]);
  const [newCard, setNewCard] = useState({ number: '', expiry: '', cvc: '', name: '' });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const formatCardNumber = (val) => {
    const clean = val.replace(/\D/g, '').slice(0, 16);
    return clean.replace(/(.{4})/g, '$1 ').trim();
  };

  const formatExpiry = (val) => {
    const clean = val.replace(/\D/g, '').slice(0, 4);
    if (clean.length >= 3) return clean.slice(0, 2) + '/' + clean.slice(2);
    return clean;
  };

  const detectBrand = (num) => {
    const clean = num.replace(/\s/g, '');
    if (/^4/.test(clean)) return 'Visa';
    if (/^5[1-5]/.test(clean)) return 'Mastercard';
    if (/^3[47]/.test(clean)) return 'Amex';
    return 'Visa';
  };

  const handleAddCard = async (e) => {
    e.preventDefault();
    setSaving(true);
    await new Promise(r => setTimeout(r, 1500));
    const brand = detectBrand(newCard.number);
    const last4 = newCard.number.replace(/\s/g, '').slice(-4);
    const card = { id: `pm_${Date.now()}`, brand, last4, expiry: newCard.expiry, isDefault: cards.length === 0 };
    setCards(prev => [...prev, card]);
    setSaving(false);
    setSuccess(true);
    setTimeout(() => { setSuccess(false); setStep('list'); setNewCard({ number: '', expiry: '', cvc: '', name: '' }); }, 1500);
  };

  const setDefault = (id) => {
    setCards(prev => prev.map(c => ({ ...c, isDefault: c.id === id })));
    const card = cards.find(c => c.id === id);
    if (card && onSave) onSave({ brand: card.brand, last4: card.last4 });
  };

  const removeCard = (id) => {
    setCards(prev => prev.filter(c => c.id !== id));
  };

  const handleClose = () => {
    setStep('list');
    setSuccess(false);
    setNewCard({ number: '', expiry: '', cvc: '', name: '' });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#0A0A0A] border-zinc-800 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white font-mono">{step === 'list' ? 'Payment Methods' : 'Add New Card'}</DialogTitle>
          <DialogDescription className="text-zinc-500 font-mono text-xs">
            {step === 'list' ? 'Manage your payment methods' : 'Enter your card details securely'}
          </DialogDescription>
        </DialogHeader>

        {step === 'list' && (
          <div className="space-y-3 mt-2">
            {cards.map(card => (
              <div key={card.id} data-testid={`payment-card-${card.id}`} className={`flex items-center gap-4 p-4 border rounded-sm transition-all ${card.isDefault ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-[#111] border-zinc-800'}`}>
                <div className="flex-shrink-0">{cardBrandIcons[card.brand] || cardBrandIcons.Visa}</div>
                <div className="flex-1">
                  <p className="text-sm font-mono text-white">{card.brand} ending in {card.last4}</p>
                  <p className="text-xs font-mono text-zinc-600">Expires {card.expiry}</p>
                </div>
                <div className="flex items-center gap-2">
                  {card.isDefault ? (
                    <span className="text-xs font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-sm border border-emerald-500/20">Default</span>
                  ) : (
                    <>
                      <button data-testid={`set-default-${card.id}`} onClick={() => setDefault(card.id)} className="text-xs font-mono text-zinc-500 hover:text-emerald-400 transition-colors">Set Default</button>
                      <button data-testid={`remove-card-${card.id}`} onClick={() => removeCard(card.id)} className="text-zinc-600 hover:text-red-400 transition-colors p-1"><Trash2 size={14} /></button>
                    </>
                  )}
                </div>
              </div>
            ))}

            <button
              data-testid="add-new-card-btn"
              onClick={() => setStep('add')}
              className="w-full flex items-center justify-center gap-2 p-4 border border-dashed border-zinc-700 rounded-sm text-sm font-mono text-zinc-500 hover:border-emerald-500/30 hover:text-emerald-400 transition-all"
            >
              <Plus size={16} /> Add New Card
            </button>
          </div>
        )}

        {step === 'add' && (
          <form onSubmit={handleAddCard} className="space-y-4 mt-2">
            {success ? (
              <div data-testid="card-add-success" className="text-center py-8">
                <CheckCircle size={40} className="text-emerald-400 mx-auto mb-3" />
                <p className="text-white font-mono">Card Added Successfully</p>
              </div>
            ) : (
              <>
                {/* Card visual preview */}
                <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-md p-5 aspect-[1.6/1] max-h-44 flex flex-col justify-between border border-zinc-700">
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-7 bg-yellow-500/30 rounded-sm" />
                    <span className="text-xs font-mono text-zinc-500">{detectBrand(newCard.number)}</span>
                  </div>
                  <div>
                    <p className="text-lg font-mono text-white tracking-wider">{newCard.number || '•••• •••• •••• ••••'}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono text-zinc-400 uppercase">{newCard.name || 'YOUR NAME'}</span>
                    <span className="text-xs font-mono text-zinc-400">{newCard.expiry || 'MM/YY'}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono text-zinc-500 mb-1.5">Card Number</label>
                  <input
                    data-testid="card-number-input"
                    type="text"
                    required
                    value={newCard.number}
                    onChange={e => setNewCard({ ...newCard, number: formatCardNumber(e.target.value) })}
                    className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/50 outline-none transition-all placeholder-zinc-600 tracking-wider"
                    placeholder="4242 4242 4242 4242"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-mono text-zinc-500 mb-1.5">Expiry</label>
                    <input
                      data-testid="card-expiry-input"
                      type="text"
                      required
                      value={newCard.expiry}
                      onChange={e => setNewCard({ ...newCard, expiry: formatExpiry(e.target.value) })}
                      className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none placeholder-zinc-600"
                      placeholder="12/28"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-mono text-zinc-500 mb-1.5">CVC</label>
                    <input
                      data-testid="card-cvc-input"
                      type="text"
                      required
                      maxLength={4}
                      value={newCard.cvc}
                      onChange={e => setNewCard({ ...newCard, cvc: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                      className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none placeholder-zinc-600"
                      placeholder="123"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-mono text-zinc-500 mb-1.5">Cardholder Name</label>
                  <input
                    data-testid="card-name-input"
                    type="text"
                    required
                    value={newCard.name}
                    onChange={e => setNewCard({ ...newCard, name: e.target.value })}
                    className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none placeholder-zinc-600"
                    placeholder="Alex Thompson"
                  />
                </div>

                <div className="flex items-center gap-2 text-xs text-zinc-600 font-mono">
                  <Shield size={12} className="text-emerald-500" /> Secured with 256-bit SSL encryption
                </div>

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setStep('list')} className="flex-1 border border-zinc-800 text-zinc-400 font-mono text-sm rounded-sm py-2.5 hover:border-zinc-700 transition-all">
                    Cancel
                  </button>
                  <button
                    data-testid="save-card-btn"
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm py-2.5 hover:bg-emerald-400 transition-all click-scale shadow-[0_0_15px_rgba(16,185,129,0.3)] disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Add Card'}
                  </button>
                </div>
              </>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function UpgradePlanModal({ open, onClose, currentPlan }) {
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);

  const plans = [
    { id: 'starter', name: 'Starter', price: 12, features: ['1 Agent', '2 vCPU', '4GB RAM', '50GB SSD'] },
    { id: 'professional', name: 'Professional', price: 29, features: ['2-10 Agents', '4 vCPU', '8GB RAM', '100GB SSD'] },
    { id: 'enterprise', name: 'Enterprise', price: 69, features: ['11-20 Agents', '8 vCPU', '16GB RAM', '200GB SSD'] },
  ];

  const handleConfirm = async () => {
    setConfirming(true);
    await new Promise(r => setTimeout(r, 1500));
    setConfirming(false);
    setDone(true);
    setTimeout(() => { setDone(false); setSelected(null); onClose(); }, 2000);
  };

  return (
    <Dialog open={open} onOpenChange={() => { setSelected(null); setDone(false); onClose(); }}>
      <DialogContent className="bg-[#0A0A0A] border-zinc-800 sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-white font-mono">Change Plan</DialogTitle>
          <DialogDescription className="text-zinc-500 font-mono text-xs">
            Current plan: {currentPlan}. Select a new plan below.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div data-testid="plan-change-success" className="text-center py-8">
            <CheckCircle size={40} className="text-emerald-400 mx-auto mb-3" />
            <p className="text-white font-mono">Plan Updated Successfully</p>
            <p className="text-sm text-zinc-500 mt-1 font-mono">Changes take effect immediately</p>
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-3 gap-3">
              {plans.map(plan => (
                <button
                  key={plan.id}
                  data-testid={`upgrade-plan-${plan.id}`}
                  onClick={() => setSelected(plan.id)}
                  disabled={plan.name === currentPlan}
                  className={`text-left p-4 border rounded-sm transition-all ${plan.name === currentPlan ? 'opacity-40 cursor-not-allowed bg-[#111] border-zinc-800' : selected === plan.id ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-[#111] border-zinc-800 hover:border-zinc-700'}`}
                >
                  <h3 className="text-sm font-mono text-white font-bold">{plan.name}</h3>
                  <p className="text-xl font-mono font-bold text-emerald-400 mt-2">${plan.price}<span className="text-xs text-zinc-500">/mo</span></p>
                  <ul className="mt-3 space-y-1">
                    {plan.features.map((f, i) => <li key={i} className="text-xs text-zinc-500 font-mono">• {f}</li>)}
                  </ul>
                  {plan.name === currentPlan && <span className="text-xs font-mono text-zinc-600 mt-2 block">Current</span>}
                </button>
              ))}
            </div>
            {selected && (
              <button
                data-testid="confirm-plan-change-btn"
                onClick={handleConfirm}
                disabled={confirming}
                className="w-full bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm py-3 hover:bg-emerald-400 transition-all click-scale shadow-[0_0_15px_rgba(16,185,129,0.3)] disabled:opacity-50"
              >
                {confirming ? 'Processing...' : `Switch to ${plans.find(p => p.id === selected)?.name}`}
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function BillingOverview() {
  const [billing, setBilling] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  useEffect(() => { api.getBillingOverview().then(setBilling); }, []);
  if (!billing) return <div className="text-zinc-500 font-mono">Loading...</div>;

  const handlePaymentSave = (method) => {
    setBilling(prev => ({ ...prev, paymentMethod: method }));
  };

  return (
    <div data-testid="billing-page" className="space-y-6">
      <h1 className="text-2xl font-bold text-white font-mono">Billing</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-6">
          <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest">Current Plan</span>
          <p className="text-2xl font-mono font-bold text-white mt-2">{billing.plan}</p>
          <p className="text-sm font-mono text-emerald-400 mt-1">${billing.amount}/mo</p>
          <button data-testid="billing-upgrade-btn" onClick={() => setUpgradeModalOpen(true)} className="mt-4 w-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-mono text-xs rounded-sm py-2 hover:bg-emerald-500/20 transition-all flex items-center justify-center gap-1">
            <ArrowUpRight size={12} /> Change Plan
          </button>
        </div>
        <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-6">
          <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest">Next Billing</span>
          <p className="text-2xl font-mono font-bold text-white mt-2">${billing.amount}.00</p>
          <p className="text-sm font-mono text-zinc-500 mt-1">{billing.nextBillingDate}</p>
        </div>
        <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-6">
          <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest">Credit Balance</span>
          <p className="text-2xl font-mono font-bold text-emerald-400 mt-2">${billing.creditBalance.toFixed(2)}</p>
          <Link to="/dashboard/billing/credits" data-testid="billing-add-credits" className="mt-4 w-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 font-mono text-xs rounded-sm py-2 hover:bg-cyan-500/20 transition-all flex items-center justify-center gap-1">
            Add Credits
          </Link>
        </div>
      </div>

      <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-6">
        <h2 className="text-sm font-mono text-zinc-400 uppercase tracking-widest mb-4">Payment Method</h2>
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0">{cardBrandIcons[billing.paymentMethod.brand] || <div className="w-12 h-8 bg-zinc-800 rounded-sm flex items-center justify-center"><CreditCard size={16} className="text-zinc-400" /></div>}</div>
          <div><p className="text-sm font-mono text-white">{billing.paymentMethod.brand} ending in {billing.paymentMethod.last4}</p><p className="text-xs font-mono text-zinc-600">Expires 12/2027</p></div>
          <button data-testid="billing-update-payment" onClick={() => setPaymentModalOpen(true)} className="ml-auto text-xs font-mono text-emerald-400 hover:underline">Update</button>
        </div>
      </div>

      <div className="flex gap-4">
        <Link to="/dashboard/billing/invoices" data-testid="billing-invoices-link" className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-5 flex-1 hover:border-zinc-700 transition-all flex items-center gap-3">
          <div className="text-left"><h3 className="text-sm font-mono text-white font-bold">View Invoices</h3><p className="text-xs text-zinc-600">Download past invoices</p></div>
        </Link>
        <Link to="/dashboard/billing/credits" data-testid="billing-credits-link" className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-5 flex-1 hover:border-zinc-700 transition-all flex items-center gap-3">
          <div className="text-left"><h3 className="text-sm font-mono text-white font-bold">Manage Credits</h3><p className="text-xs text-zinc-600">Purchase and track credits</p></div>
        </Link>
      </div>

      <PaymentMethodModal open={paymentModalOpen} onClose={() => setPaymentModalOpen(false)} currentMethod={billing.paymentMethod} onSave={handlePaymentSave} />
      <UpgradePlanModal open={upgradeModalOpen} onClose={() => setUpgradeModalOpen(false)} currentPlan={billing.plan} />
    </div>
  );
}
